import type { LoadStatusAliasSchema } from '@repo/schema/load'

import { DEFAULT_LOADS_TAB } from './loads-query-params'

type LoadStatusAlias = (typeof LoadStatusAliasSchema.options)[number]

// Status group constants (from status-steps.ts)
export const PLANNING_STATUSES = ['to_cover', 'transport_planning', 'waiting_for_transit'] as const

export const TRANSIT_STATUSES = [
  'waiting_for_arrival_confirmation',
  'arrived_at_collection',
  'loading_started',
  'in_transit',
  'arrived_at_destination',
  'unloading_confirmed',
] as const

export const POST_DELIVERY_STATUSES = ['waiting_for_docs', 'completed'] as const

// Active is the work-in-progress pipeline: it excludes the three statuses that
// own a tab or a view of their own — 'draft', 'archived' and 'cancelled'.
export const ACTIVE_STATUSES = [
  ...PLANNING_STATUSES,
  ...TRANSIT_STATUSES,
  ...POST_DELIVERY_STATUSES,
] as const satisfies readonly LoadStatusAlias[]

export const ARCHIVED_STATUSES = ['archived'] as const

// Map tab names to their status groups
export const TAB_STATUS_GROUPS = {
  active: ACTIVE_STATUSES,
  archived: ARCHIVED_STATUSES,
  draft: ['draft'],
  planning: PLANNING_STATUSES,
  post_delivery: POST_DELIVERY_STATUSES,
  transit: TRANSIT_STATUSES,
} as const

/**
 * `Object.hasOwn` rather than `in`: the `in` operator walks the prototype
 * chain, so a tab named 'toString' or 'constructor' would resolve to a function
 * instead of a status group. 'all' is a real tab that simply owns no group.
 */
export const isStatusGroupTab = (tab: string): tab is keyof typeof TAB_STATUS_GROUPS =>
  Object.hasOwn(TAB_STATUS_GROUPS, tab)

/** Tab names, as the tab strip shows them. */
export const LOADS_TAB_TITLES = {
  active: 'Active',
  all: 'All',
  archived: 'Archived',
  draft: 'Draft',
  planning: 'Planning',
  post_delivery: 'Post-delivery',
  transit: 'Transit',
} as const

export type LoadsTab = keyof typeof LOADS_TAB_TITLES

const isKnownLoadsTab = (tab: string): tab is LoadsTab => Object.hasOwn(LOADS_TAB_TITLES, tab)

/**
 * Resolves the tab a request belongs to. Anything the tab strip cannot show —
 * a hand-edited URL, an empty value, the `number` the search-param record
 * allows — falls back to the default rather than going through unfiltered.
 */
export const resolveLoadsTab = (status: number | string | undefined): LoadsTab => {
  const tab = String(status ?? '')
  return isKnownLoadsTab(tab) ? tab : DEFAULT_LOADS_TAB
}
